import * as React from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, Dropdown, TextField, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import type { IDropdownOption } from '@fluentui/react';

import { PBTPauseReasonOptions, PBTMessages, CaseActionNotesMinLength } from '../../../Services/PBTConstants';

export interface IPauseCaseDialogProps {
  isOpen: boolean;
  caseName: string;
  onConfirm: (reason: string, reasonText?: string) => Promise<void>;
  onDismiss: () => void;
}

export const PauseCaseDialog: React.FC<IPauseCaseDialogProps> = ({ isOpen, caseName, onConfirm, onDismiss }) => {
  const [reason, setReason] = React.useState<string>('');
  const [reasonText, setReasonText] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // The parent closes the dialog (isOpen -> false) on a successful confirm
  // without calling handleDismiss, so reset the fields whenever it closes to
  // avoid carrying stale reason/notes into the next open.
  React.useEffect(() => {
    if (!isOpen) {
      setReason('');
      setReasonText('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const isOther = reason === 'Other';
  const trimmedNotes = reasonText.trim();
  const isValid = reason !== '' && (!isOther || trimmedNotes.length >= CaseActionNotesMinLength);
  const notesError =
    isOther && trimmedNotes.length > 0 && trimmedNotes.length < CaseActionNotesMinLength
      ? PBTMessages.CaseActionNotesTooShort(CaseActionNotesMinLength)
      : undefined;

  const handleConfirm = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason, isOther ? trimmedNotes : undefined);
    } catch (err) {
      setError((err as Error).message || 'Failed to pause case.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setReason('');
    setReasonText('');
    setError(null);
    onDismiss();
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Pause Case',
        subText: `Pausing "${caseName}" will freeze all automated actions (emails, checks, attestation). You can resume the case later.`,
      }}
      modalProps={{ isBlocking: true }}
      minWidth={480}
    >
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <Dropdown
        label="Pause Reason"
        required
        placeholder="Select a reason"
        options={PBTPauseReasonOptions as unknown as IDropdownOption[]}
        selectedKey={reason}
        onChange={(_, option) => setReason((option?.key as string) || '')}
        disabled={isSubmitting}
      />

      {isOther && (
        <TextField
          label="Additional Notes"
          required
          multiline
          rows={3}
          value={reasonText}
          onChange={(_, v) => setReasonText(v || '')}
          placeholder="Please provide details..."
          description={`Minimum ${CaseActionNotesMinLength} characters.`}
          errorMessage={notesError}
          disabled={isSubmitting}
        />
      )}

      <DialogFooter>
        {isSubmitting ? (
          <Spinner size={SpinnerSize.small} label="Pausing..." />
        ) : (
          <>
            <PrimaryButton text="Confirm Pause" onClick={handleConfirm} disabled={!isValid} />
            <DefaultButton text="Cancel" onClick={handleDismiss} />
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
};
